// src/app/components/header/Header.js

import React, { startTransition, useMemo, useState, useRef, useCallback, useEffect } from "react";
import styles from "./Header.module.css";
import zorniLogo from "../../../assets/img/core-img/zorni-logo.png";

import { useZorni } from "../../hooks/useZorni";
import { usePerson } from "../../hooks/user/usePerson";
import { useNotifications } from "../../hooks/NotificationContext";
import { useNavigate, useLocation } from "react-router-dom";
import { useHeaderDock } from "../../providers/HeaderDockContext";

import TranslateToggle, { LANGUAGES, shortCode } from "./translateToggle/TranslateToggle";
import ProfilToggle from "./profilToggle/ProfilToggle";
import useReactSimpleMatchMedia from "react-simple-matchmedia";
import { useTranslation } from "react-i18next";

import {
  NavBar,
  SearchBar,
  SearchSummary,
  Button,
  Icon,
} from "@zorni/components-lib";

import { generateRegistrationurl } from "../../auth/regestration";
import { USER_TYPE } from "../../../common/model/enum";

import SearchForm from "../searshForms/searchForm/SearchForm";
import { useSearch } from "../../hooks/SearchContext";

// ✅ NEW
import { useDismiss } from "../../hooks/useDismiss";

/**
 * Menu invité (utilisateur pas connecté)
 */
function GuestMenu({ onLogin }) {
  return (
    <div className={styles.dropdownPanel}>
      <button className={styles.dropdownItem} onClick={onLogin}>
        <Icon name="iconUser" className={styles.dropdownIcon} />
        <span>Se connecter / S’inscrire</span>
      </button>

      <div className={styles.dropdownDivider} />

      <button
        className={styles.dropdownItem}
        onClick={() => {
          window.open("/help", "_self");
        }}
      >
        <Icon name="iconInfo" className={styles.dropdownIcon} />
        <span>Centre d’aide</span>
      </button>
    </div>
  );
}

export function Header() {
  const navigate = useNavigate();
  const location = useLocation();
  const { t, i18n } = useTranslation();

  /* Pastille de langue : on affiche le code actif plutôt qu'un globe, pour
     que l'utilisateur sache dans quelle langue il est sans rien ouvrir. */
  const currentLanguageCode = shortCode(i18n.language);
  const currentLanguageLabel =
    LANGUAGES.find((language) => language.code === String(i18n.language || "").split("-")[0])
      ?.label ?? currentLanguageCode;
  const { isAuthenticated, keycloak, type } = useZorni();
  const { personInfo } = usePerson();
  const { hasUnreadNotifications } = useNotifications();
  const { docked } = useHeaderDock();
  const isHomePage = location.pathname === "/";
  // Pages où la barre compacte se docke dans l'en-tête (formulaire hors écran).
  const hasSearchDock = isHomePage || location.pathname === "/trips";

  // 🔗 état global de recherche + sheet mobile partagée avec MobileBottomNav
  const { search, updateSearch, resetSearch, sheetOpen: openSearchSheet, setSheetOpen: setOpenSearchSheet } = useSearch();
  // Téléphone uniquement : toutes les tablettes (iPad mini 744px compris)
  // gardent le header desktop (logo + actions), pas le header mobile simplifié.
  const isPhone = useReactSimpleMatchMedia("(max-width: 599.98px)");
  // Assez large pour la barre de recherche complète (3 segments) dockée ;
  // en dessous (tablettes portrait), on affiche un déclencheur compact.
  const isFullSearchBar = useReactSimpleMatchMedia("(min-width: 1024px)");
  const isComfortDesktop = useReactSimpleMatchMedia("(min-width: 1181px)");
  // Recherche dépliée dans l'en-tête : champ ouvert en premier, et une clé
  // qui change à chaque demande pour rouvrir le même champ.
  const [openSearchPanel, setOpenSearchPanel] = useState(false);
  const [searchField, setSearchField] = useState("dest");
  const [searchOpenKey, setSearchOpenKey] = useState(0);

  // autres menus
  const [openLang, setOpenLang] = useState(false);
  const [openGuestMenu, setOpenGuestMenu] = useState(false);
  const [openProfileMenu, setOpenProfileMenu] = useState(false);

  // ✅ REFS wrappers (englobe trigger + dropdown)
  const langWrapperRef = useRef(null);
  const guestWrapperRef = useRef(null);
  const profileWrapperRef = useRef(null);
  const shouldScrollHomeRef = useRef(false);

  const closeAllDropdownMenus = useCallback(() => {
    setOpenLang(false);
    setOpenGuestMenu(false);
    setOpenProfileMenu(false);
  }, []);

  // ✅ click outside + ESC
  useDismiss({
    open: openLang,
    panelRef: langWrapperRef,
    onClose: () => setOpenLang(false),
  });

  useDismiss({
    open: openGuestMenu,
    panelRef: guestWrapperRef,
    onClose: () => setOpenGuestMenu(false),
  });

  useDismiss({
    open: openProfileMenu,
    panelRef: profileWrapperRef,
    onClose: () => setOpenProfileMenu(false),
  });

  const submitSearch = () => {
    const {
      destination = "",
      startDate = "",
      endDate = "",
      adults = 0,
      children = 0,
      babies = 0,
    } = search || {};

    // Seuls les critères renseignés partent dans l'URL.
    const params = new URLSearchParams(
      Object.entries({
        destination,
        startDate,
        endDate,
        adult: adults,
        child: children,
        baby: babies,
      }).filter(([, v]) => v !== "" && v !== 0 && v != null)
    ).toString();
    const url = `/trips${params ? `?${params}` : ""}`;
    const courante = `${location.pathname}${location.search}`;

    /* Même destination que la page courante — typiquement la loupe cliquée
       sans aucun critère depuis les résultats : naviguer ne produirait rien.
       On ouvre le panneau pour que le clic ait une suite visible. */
    if (url === courante) {
      openSearch("dest");
      return;
    }

    setOpenSearchSheet(false);
    setOpenSearchPanel(false);
    startTransition(() => {
      navigate(url);
    });
  };

  const openSheet = () => {
    setOpenSearchSheet(true);
    closeAllDropdownMenus();
  };

  const openSearch = useCallback(
    (field = "dest") => {
      if (!hasSearchDock) return;
      closeAllDropdownMenus();
      setSearchField(field);
      setSearchOpenKey((k) => k + 1);
      setOpenSearchPanel(true);
    },
    [hasSearchDock, closeAllDropdownMenus]
  );

  const closeSearch = useCallback(() => setOpenSearchPanel(false), []);

  const togglePanel = () => {
    if (openSearchPanel) closeSearch();
    else openSearch("dest");
  };

  // Échap referme la recherche dépliée ; une navigation aussi.
  useEffect(() => {
    if (!openSearchPanel) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape") closeSearch();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [openSearchPanel, closeSearch]);

  useEffect(() => {
    setOpenSearchPanel(false);
  }, [location.key]);

  useEffect(() => {
    if (!hasSearchDock) {
      setOpenSearchPanel(false);
    }
  }, [hasSearchDock]);

  useEffect(() => {
    if (!shouldScrollHomeRef.current || !isHomePage) return;

    window.scrollTo({ top: 0, behavior: "smooth" });
    shouldScrollHomeRef.current = false;
  }, [isHomePage, location.key]);

  // avatar initiale
  const avatarInitial = useMemo(() => {
    const first = personInfo?.firstName || personInfo?.lastName || "";
    return first ? first.trim().charAt(0).toUpperCase() : "•";
  }, [personInfo]);

  const avatarPicture = personInfo?.picture || "";

  const handleLogin = () => {
    keycloak.login();
  };

  const handleBecomePartner = () => {
    const registrationURL = generateRegistrationurl(keycloak);
    window.location.href = registrationURL;
  };

  const handleDashboard = () => {
    startTransition(() => {
      navigate("/admin");
    });
  };

  const goProfilePage = () => {
    if (!isAuthenticated) {
      keycloak.login();
      return;
    }
    startTransition(() => {
      navigate("/profil");
    });
  };

  const toggleLang = () => {
    setOpenLang((v) => !v);
    setOpenGuestMenu(false);
    setOpenProfileMenu(false);
    setOpenSearchPanel(false);
  };

  const toggleGuestMenu = () => {
    setOpenGuestMenu((v) => !v);
    setOpenLang(false);
    setOpenProfileMenu(false);
    setOpenSearchPanel(false);
  };

  const toggleProfileMenu = () => {
    setOpenProfileMenu((v) => !v);
    setOpenLang(false);
    setOpenGuestMenu(false);
    setOpenSearchPanel(false);
  };

  const handleBrandClick = () => {
    closeAllDropdownMenus();
    setOpenSearchPanel(false);
    setOpenSearchSheet(false);

    if (isHomePage) {
      window.scrollTo({ top: 0, behavior: "smooth" });
      return;
    }

    shouldScrollHomeRef.current = true;
    startTransition(() => {
      navigate("/");
    });
  };

  // ====== MOBILE (téléphone uniquement) ======
  if (isPhone) {
    return (
      <>
      <NavBar.Root overlay={!docked} solid={docked} maxWidth="fluid">
        <NavBar.Left>
          <button
            className={styles.brand}
            onClick={handleBrandClick}
            aria-label="Accueil"
          >
            <img src={zorniLogo} alt="Zorni" />
          </button>
        </NavBar.Left>

        <NavBar.Right>
          <div
            ref={langWrapperRef}
            className={`${styles.menuWrapper} ${styles.menuWrapperRight}`}
            data-open={openLang ? "true" : "false"}
          >
            <button
              type="button"
              className={`${styles.circleIconButton} ${styles.langButton}`}
              aria-label={t("language.current", {
                language: currentLanguageLabel,
                defaultValue: `Langue : ${currentLanguageLabel}`,
              })}
              aria-expanded={openLang}
              onClick={toggleLang}
            >
              {currentLanguageCode}
            </button>
            <div className={styles.dropdownInner}>
              <TranslateToggle onSelect={() => setOpenLang(false)} />
            </div>
          </div>
        </NavBar.Right>

      </NavBar.Root>

      {/* Récapitulatif replié sous l'en-tête quand celui de la page n'est
          plus visible (accueil, résultats). */}
      {hasSearchDock && (
        <div
          className={`${styles.phoneSearchDock} ${docked ? styles.phoneSearchDockVisible : ""}`}
          aria-hidden={!docked}
          inert={docked ? undefined : ""}
        >
          <SearchSummary
            variant="compact"
            value={search}
            onClick={openSheet}
            t={t}
          />
        </div>
      )}
      </>
    );
  }

  // ====== DESKTOP ======
  return (
    <>
      <NavBar.Root overlay={!docked} solid={docked} maxWidth="contained">
        <NavBar.Left>
          <button
            className={styles.brand}
            onClick={handleBrandClick}
            aria-label="Accueil"
          >
            <img src={zorniLogo} alt="Zorni" />
          </button>
        </NavBar.Left>

        <NavBar.Center>
          {docked && isFullSearchBar && (
            <SearchBar
              value={{
                destination: search.destination,
                startDate: search.startDate,
                endDate: search.endDate,
                adults: search.adults,
                children: search.children,
                babies: search.babies,
              }}
              onSegmentClick={openSearch}
              onSubmit={submitSearch}
              hidden={openSearchPanel}
              t={t}
            />
          )}
          {docked && !isFullSearchBar && (
            <button
              type="button"
              className={styles.mobileSearch}
              onClick={togglePanel}
              aria-label={t("components.search.searchLabel", "Rechercher")}
            >
              <Icon name="iconSearch" size="sm" />
              {search.destination || t("components.search.searchLabel", "Rechercher")}
            </button>
          )}
        </NavBar.Center>

        <NavBar.Right>
          {!isAuthenticated && (
            <>
              {isComfortDesktop && (
                <Button
                  variant="secondary"
                  size="small"
                  iconName="iconHandshake"
                  iconPosition="left"
                  label="Devenir partenaire"
                  onClick={handleBecomePartner}
                  className={styles.partnerCta}
                />
              )}

              <div
                ref={langWrapperRef}
                className={`${styles.menuWrapper} ${styles.menuWrapperRight}`}
                data-open={openLang ? "true" : "false"}
              >
                <button
                  type="button"
                  className={`${styles.circleIconButton} ${styles.langButton}`}
                  aria-label={t("language.current", {
                    language: currentLanguageLabel,
                    defaultValue: `Langue : ${currentLanguageLabel}`,
                  })}
                  aria-expanded={openLang}
                  onClick={toggleLang}
                >
                  {currentLanguageCode}
                </button>
                <div className={styles.dropdownInner}>
                  <TranslateToggle onSelect={() => setOpenLang(false)} />
                </div>
              </div>

              <div
                ref={guestWrapperRef}
                className={`${styles.menuWrapper} ${styles.menuWrapperRight}`}
                data-open={openGuestMenu ? "true" : "false"}
              >
                <button
                  className={styles.circleIconButton}
                  onClick={toggleGuestMenu}
                  aria-label="Menu compte"
                >
                  <Icon name="iconListe" />
                </button>

                <div className={styles.dropdownInner}>
                  <GuestMenu onLogin={handleLogin} />
                </div>
              </div>
            </>
          )}

          {isAuthenticated && (
            <>
              {type === USER_TYPE.PRO && (
                <Button
                  variant="secondary"
                  size="small"
                  iconName="iconGrid"
                  iconPosition="left"
                  label={isComfortDesktop ? "Dashboard" : undefined}
                  aria-label="Dashboard"
                  onClick={handleDashboard}
                  className={styles.partnerCta}
                />
              )}

              <div
                ref={langWrapperRef}
                className={`${styles.menuWrapper} ${styles.menuWrapperRight}`}
                data-open={openLang ? "true" : "false"}
              >
                <button
                  type="button"
                  className={`${styles.circleIconButton} ${styles.langButton}`}
                  aria-label={t("language.current", {
                    language: currentLanguageLabel,
                    defaultValue: `Langue : ${currentLanguageLabel}`,
                  })}
                  aria-expanded={openLang}
                  onClick={toggleLang}
                >
                  {currentLanguageCode}
                </button>
                <div className={styles.dropdownInner}>
                  <TranslateToggle onSelect={() => setOpenLang(false)} />
                </div>
              </div>

              <button
                className={styles.avatarChip}
                onClick={goProfilePage}
                aria-label="Mon profil"
              >
                {avatarPicture ? (
                  <img
                    src={avatarPicture}
                    alt="Photo de profil"
                    className={styles.avatarImage}
                  />
                ) : (
                  avatarInitial
                )}
              </button>

              <div
                ref={profileWrapperRef}
                className={`${styles.menuWrapper} ${styles.menuWrapperRight}`}
                data-open={openProfileMenu ? "true" : "false"}
              >
                <button
                  className={styles.circleIconButton}
                  onClick={toggleProfileMenu}
                  aria-label="Ouvrir le menu utilisateur"
                >
                  <Icon name="iconListe" />
                  {hasUnreadNotifications && (
                    <span
                      className={styles.notificationDot}
                      aria-label="Notifications non lues"
                    />
                  )}
                </button>

                <div className={styles.dropdownInner}>
                  <ProfilToggle onSelect={() => setOpenProfileMenu(false)} />
                </div>
              </div>
            </>
          )}
        </NavBar.Right>
      </NavBar.Root>

      {/* Recherche dépliée : voile sur la page + formulaire sous l'en-tête */}
      {openSearchPanel && (
        <div
          className={styles.searchScrim}
          onClick={closeSearch}
          aria-hidden="true"
        />
      )}

      {openSearchPanel && (
        <div
          className={styles.searchPanel}
          role="dialog"
          aria-label={t("search.submit", "Rechercher")}
        >
          <div className={styles.searchPanelInner}>
            <SearchForm
              variant="header"
              value={search}
              onChange={(patch) => updateSearch(patch)}
              autoOpenField={searchField}
              autoOpenKey={searchOpenKey}
              onSearch={closeSearch}
            />
          </div>
        </div>
      )}
    </>
  );
}

export default Header;
